import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, memo } from 'react';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next';
import noop from '@atlaskit/ds-lib/noop';
import useControlled from '@atlaskit/ds-lib/use-controlled';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import { Box, Inline } from '@atlaskit/primitives';
import Navigator from './internal/components/navigator';
import PageComponent from './internal/components/page';
import renderDefaultEllipsis from './internal/components/render-ellipsis';
import { emptyObject } from './internal/constants';
import collapseRange from './internal/utils/collapse-range';
var analyticsAttributes = {
  componentName: 'pagination',
  packageName: "@atlaskit/pagination",
  packageVersion: "14.5.1"
};
function InnerPagination(_ref, ref) {
  var _ref$components = _ref.components,
    components = _ref$components === void 0 ? emptyObject : _ref$components,
    _ref$defaultSelectedI = _ref.defaultSelectedIndex,
    defaultSelectedIndex = _ref$defaultSelectedI === void 0 ? 0 : _ref$defaultSelectedI,
    selectedIndex = _ref.selectedIndex,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'pagination' : _ref$label,
    _ref$pageLabel = _ref.pageLabel,
    pageLabel = _ref$pageLabel === void 0 ? 'page' : _ref$pageLabel,
    _ref$previousLabel = _ref.previousLabel,
    previousLabel = _ref$previousLabel === void 0 ? 'previous' : _ref$previousLabel,
    _ref$nextLabel = _ref.nextLabel,
    nextLabel = _ref$nextLabel === void 0 ? 'next' : _ref$nextLabel,
    _ref$style = _ref.style,
    style = _ref$style === void 0 ? emptyObject : _ref$style,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? 7 : _ref$max,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? noop : _ref$onChange,
    pages = _ref.pages,
    getPageLabel = _ref.getPageLabel,
    _ref$renderEllipsis = _ref.renderEllipsis,
    renderEllipsis = _ref$renderEllipsis === void 0 ? renderDefaultEllipsis : _ref$renderEllipsis,
    analyticsContext = _ref.analyticsContext,
    testId = _ref.testId,
    isDisabled = _ref.isDisabled;
  var _useControlled = useControlled(selectedIndex, function () {
      return defaultSelectedIndex || 0;
    }),
    _useControlled2 = _slicedToArray(_useControlled, 2),
    selectedIndexValue = _useControlled2[0],
    setSelectedIndexValue = _useControlled2[1];
  var onChangeWithAnalytics = usePlatformLeafEventHandler(_objectSpread({
    fn: function fn(value, analyticsEvent) {
      var event = value.event,
        selectedPageIndex = value.selectedPageIndex;
      if (selectedIndex === undefined) {
        setSelectedIndexValue(selectedPageIndex);
      }
      if (onChange) {
        onChange(event, pages[selectedPageIndex], analyticsEvent);
      }
    },
    action: 'changed',
    actionSubject: 'pageNumber',
    analyticsData: analyticsContext
  }, analyticsAttributes));
  var transform = function transform(page, currPageIndex, testId) {
    var selectedPage = pages[selectedIndexValue];
    var pageIndexLabel = "".concat(pageLabel, " ").concat(getPageLabel ? getPageLabel(page, currPageIndex) : page);
    return /*#__PURE__*/React.createElement(PageComponent, {
      key: "page-".concat(getPageLabel ? getPageLabel(page, currPageIndex) : currPageIndex),
      component: components.Page,
      onClick: function onClick(event) {
        return onChangeWithAnalytics({
          event: event,
          selectedPageIndex: currPageIndex
        });
      },
      "aria-current": page === selectedPage ? 'page' : undefined,
      "aria-label": pageIndexLabel,
      isSelected: page === selectedPage,
      isDisabled: isDisabled,
      page: page,
      testId: testId && "".concat(testId, "--").concat(page === selectedPage ? 'current-' : '', "page-").concat(currPageIndex)
    }, getPageLabel ? getPageLabel(page, currPageIndex) : page);
  };
  return /*#__PURE__*/React.createElement(Box, {
    testId: testId,
    style: style,
    ref: ref,
    "aria-label": label,
    as: "nav"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.0",
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(Navigator, {
    key: "left-navigator",
    component: components.Previous,
    onClick: function onClick(event) {
      return onChangeWithAnalytics({
        event: event,
        selectedPageIndex: selectedIndexValue - 1
      });
    },
    isDisabled: isDisabled || selectedIndexValue === 0,
    iconBefore: /*#__PURE__*/React.createElement(ChevronLeftLargeIcon, {
      label: ""
    }),
    "aria-label": previousLabel,
    pages: pages,
    testId: testId && "".concat(testId, "--left-navigator")
  }), collapseRange(pages, selectedIndexValue, {
    max: max,
    ellipsis: renderEllipsis,
    transform: transform
  }, testId), /*#__PURE__*/React.createElement(Navigator, {
    key: "right-navigator",
    component: components.Next,
    onClick: function onClick(event) {
      return onChangeWithAnalytics({
        event: event,
        selectedPageIndex: selectedIndexValue + 1
      });
    },
    isDisabled: isDisabled || selectedIndexValue === pages.length - 1,
    iconBefore: /*#__PURE__*/React.createElement(ChevronRightLargeIcon, {
      label: ""
    }),
    "aria-label": nextLabel,
    pages: pages,
    testId: testId && "".concat(testId, "--right-navigator")
  })));
}
var Pagination = /*#__PURE__*/forwardRef(InnerPagination);
export default /*#__PURE__*/memo(Pagination);